import React from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Typography } from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import HearingRoundedIcon from '@mui/icons-material/HearingRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import { CustomSentenceApi } from '../api/CustomSentenceApi';
import { SentenceApi } from '../api/SentenceApi';
import { IOpenResponsePronunciation, IOpenResponseSpanAnchor, IStudentOpenResponseAttempt, VerbaScholaApi } from '../api/VerbaScholaApi';
import StudentPronunciationDetail, { StudentPronunciationLegend } from './StudentPronunciationDetail';
import { StudentCautionNote, StudentDisclosureButton } from './StudentInstructionPresentation';
import { instructionalPinyin } from './studentInstructionalPinyin';
import { STUDENT_BLUE, STUDENT_BLUE_TEXT, STUDENT_CARD_RADIUS, STUDENT_ERROR_TEXT, STUDENT_GREEN, STUDENT_INTER, STUDENT_NAVY, STUDENT_POPPINS, STUDENT_RED,
    STUDENT_RING, STUDENT_SLATE, STUDENT_SUCCESS_TEXT, STUDENT_SURFACE, STUDENT_TILE_RADIUS, STUDENT_YELLOW, studentBlueButtonSx, studentEyebrowSx, studentSecondaryButtonSx } from '../style/verbaScholaStudentStyles';

interface Props {
    stepId: number;
    attempt: IStudentOpenResponseAttempt;
    speakOnly: boolean;
    // Moves the student to the writing or the recorder; absent once the answer is final.
    onRevise?: () => void;
}

interface LanguageItem extends IOpenResponseSpanAnchor {
    heading: string;
    original_span: string;
    revised: string;
    explanation: string;
    new_example_zh?: string;
    new_example_en?: string;
    priority?: number;
}

const sectionTitleSx = { fontFamily: STUDENT_POPPINS, fontWeight: 600, fontSize: 16, color: STUDENT_NAVY } as const;
const bodySx = { fontFamily: STUDENT_INTER, fontSize: 14, lineHeight: 1.55, color: STUDENT_NAVY } as const;
const noteSx = { fontFamily: STUDENT_INTER, fontSize: 12, lineHeight: 1.5, color: STUDENT_SLATE } as const;

const unscoredMessages: Record<string, string> = {
    not_in_script: 'This part is not in your writing, so its sounds were not checked. Play it to hear what you said.',
    no_speech: 'No speech was heard in this part of the recording, so it was not checked.',
    target_mismatch: 'This part of the recording did not match the text closely enough to check.',
    skipped_too_long: 'This sentence was too long to check. Try shorter sentences.',
};

export function languageItems(attempt: IStudentOpenResponseAttempt): LanguageItem[] {
    if (!attempt.feedback) return [];
    return [
        ...(attempt.feedback.corrections ?? []).map(item => ({ ...item, heading: 'A correction', revised: item.corrected_sentence, explanation: item.teaching_moment })),
        ...(attempt.feedback.naturalness_suggestions ?? []).map(item => ({ ...item, heading: 'Another way to say it', revised: item.suggested_sentence, explanation: item.reason })),
    ].sort((a, b) => (a.priority ?? 1000) - (b.priority ?? 1000));
}

interface DiffPart { kind: 'same' | 'removed' | 'added'; text: string }

// Character-level diff (longest common subsequence) from the student's sentence to the
// suggested one; at a replacement the removed text comes before the added text.
function diffChars(before: string, after: string): DiffPart[] {
    const a = Array.from(before);
    const b = Array.from(after);
    const common = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            common[i][j] = a[i] === b[j] ? common[i + 1][j + 1] + 1 : Math.max(common[i + 1][j], common[i][j + 1]);
        }
    }
    const parts: DiffPart[] = [];
    const push = (kind: DiffPart['kind'], text: string) => {
        const last = parts[parts.length - 1];
        if (last && last.kind === kind) last.text += text;
        else parts.push({ kind, text });
    };
    let i = 0;
    let j = 0;
    while (i < a.length || j < b.length) {
        if (i < a.length && j < b.length && a[i] === b[j]) {
            push('same', a[i++]);
            j++;
        } else if (i < a.length && (j === b.length || common[i + 1][j] >= common[i][j + 1])) {
            push('removed', a[i++]);
        } else {
            push('added', b[j++]);
        }
    }
    return parts;
}

/** One change to a sentence: characters [start, end) of the original replaced by text. */
interface SentenceEdit { start: number; end: number; text: string; numbers: number[] }

// A suggested sentence as separate changes to the student's sentence (character positions).
function sentenceEdits(before: string, after: string, number: number): SentenceEdit[] {
    const edits: SentenceEdit[] = [];
    let index = 0;
    let current: SentenceEdit | null = null;
    for (const part of diffChars(before, after)) {
        const length = Array.from(part.text).length;
        if (part.kind === 'same') { current = null; index += length; continue; }
        if (!current) { current = { start: index, end: index, text: '', numbers: [number] }; edits.push(current); }
        if (part.kind === 'removed') { current.end += length; index += length; } else current.text += part.text;
    }
    return edits;
}

const sameEdit = (a: SentenceEdit, b: SentenceEdit) => a.start === b.start && a.end === b.end && a.text === b.text;
// Changes clash when they overlap or touch: adding 很 before 喜欢 and replacing 喜欢 both
// change the same words, so they are not combined.
const clashes = (a: SentenceEdit, b: SentenceEdit) => a.start <= b.end && b.start <= a.end;

/** The language notes that quote one sentence of the answer. Each note's suggested sentence makes
 *  only its own change, so the changes are combined into one sentence; a note whose change
 *  overlaps an earlier one keeps its own suggested sentence instead. */
interface NoteGroup {
    sentence: string | null;
    // The sentence's place in the answer text, in UTF-16 units, without surrounding spaces.
    start: number | null;
    end: number | null;
    // heard: an unplaced audio-only note's recording, as transcribed.
    notes: { item: LanguageItem; number: number; separate: boolean; heard?: string }[];
    edits: SentenceEdit[];
}

function applyEdits(sentence: string, edits: SentenceEdit[]): string {
    const characters = Array.from(sentence);
    let text = '';
    let cursor = 0;
    for (const edit of [...edits].sort((a, b) => a.start - b.start)) {
        text += characters.slice(cursor, edit.start).join('') + edit.text;
        cursor = edit.end;
    }
    return text + characters.slice(cursor).join('');
}

// A note placed in a written sentence changes only its quoted words there (place_revision). A server from before
// place_* sends only sentence_start/sentence_end, to be replaced with the corrected sentence itself.
const placedRevision = (item: LanguageItem) => item.place_revision ?? item.revised;
const placedRange = (item: LanguageItem): [number, number] | null => item.place_start != null && item.place_end != null
    ? [item.place_start, item.place_end]
    : item.sentence_start != null && item.sentence_end != null ? [item.sentence_start, item.sentence_end] : null;

export function noteGroups(items: LanguageItem[], answerText: string): NoteGroup[] {
    const groups = new Map<string, NoteGroup>();
    items.forEach((item, index) => {
        const number = index + 1;
        const range = placedRange(item);
        const raw = range ? answerText.slice(range[0], range[1]) : '';
        const sentence = raw.trim();
        const start = sentence && range ? range[0] + raw.length - raw.trimStart().length : null;
        const key = sentence && range ? `${range[0]}:${range[1]}` : `note:${number}`;
        const group = groups.get(key) ?? { sentence: sentence || null, start, end: start != null ? start + sentence.length : null, notes: [], edits: [] };
        groups.set(key, group);
        let separate = false;
        if (group.sentence) {
            const edits = sentenceEdits(group.sentence, placedRevision(item), number);
            const shared = edits.map(edit => group.edits.find(existing => sameEdit(existing, edit)));
            separate = edits.some((edit, position) => !shared[position] && group.edits.some(existing => clashes(existing, edit)));
            if (!separate) edits.forEach((edit, position) => {
                const existing = shared[position];
                if (existing) existing.numbers.push(number); else group.edits.push(edit);
            });
        }
        const heard = item.recording_start != null && item.recording_end != null
            ? answerText.slice(item.recording_start, item.recording_end).trim() || undefined : undefined;
        group.notes.push({ item, number, separate, heard });
    });
    return [...groups.values()];
}

// Screen-reader-only text. Pixel units are explicit: in sx, width 1 would mean 100%.
const visuallyHidden = { position: 'absolute', width: '1px', height: '1px', m: '-1px', p: 0, border: 0, overflow: 'hidden',
    clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' } as const;
const removedSx = { color: STUDENT_ERROR_TEXT, textDecorationThickness: '1.5px' } as const;
const addedSx = { px: '1px', borderRadius: '3px', bgcolor: `${STUDENT_GREEN}1F`, color: STUDENT_SUCCESS_TEXT, fontWeight: 700, textDecoration: 'none' } as const;

function Removed({ text }: { text: string }) {
    return <Box component="del" sx={removedSx}><Box component="span" sx={visuallyHidden}>removed: </Box>{text}</Box>;
}

function Added({ text }: { text: string }) {
    return <Box component="ins" sx={addedSx}><Box component="span" sx={visuallyHidden}>added: </Box>{text}</Box>;
}

function SentenceChange({ before, after }: { before: string; after: string }) {
    return <Typography lang="zh" sx={{ ...bodySx, fontSize: 16, mt: 0.25 }}>
        {diffChars(before, after).map((part, index) => part.kind === 'removed' ? <Removed key={index} text={part.text} />
            : part.kind === 'added' ? <Added key={index} text={part.text} />
                : <React.Fragment key={index}>{part.text}</React.Fragment>)}
    </Typography>;
}

// The student's sentence with every combined change marked and tagged with its note number.
function CombinedChange({ sentence, edits }: { sentence: string; edits: SentenceEdit[] }) {
    const characters = Array.from(sentence);
    const parts: React.ReactNode[] = [];
    let cursor = 0;
    for (const edit of [...edits].sort((a, b) => a.start - b.start)) {
        if (edit.start > cursor) parts.push(characters.slice(cursor, edit.start).join(''));
        // Long changes wrap like the rest of the sentence; a word joiner (U+2060) keeps the note
        // number on the line of the change it marks, without splitting the change in two.
        parts.push(<React.Fragment key={edit.start}>
            {edit.end > edit.start && <Removed text={characters.slice(edit.start, edit.end).join('')} />}
            {edit.text && <Added text={edit.text} />}
            {'⁠'}<NoteMark numbers={edit.numbers} label="note" />
        </React.Fragment>);
        cursor = edit.end;
    }
    parts.push(characters.slice(cursor).join(''));
    return <Typography lang="zh" sx={{ ...bodySx, fontSize: 16 }}>{parts}</Typography>;
}

// A superscript note number, named for screen readers by hidden text (a superscript cannot carry a label).
function NoteMark({ numbers, label }: { numbers: number[]; label: string }) {
    return <>
        <Box component="sup" aria-hidden="true" sx={{ ml: '1px', fontFamily: STUDENT_INTER, fontWeight: 700, fontSize: 11, color: STUDENT_BLUE_TEXT }}>{numbers.join(',')}</Box>
        <Box component="span" sx={visuallyHidden}>{` (${label}${numbers.length > 1 ? 's' : ''} ${joinNumbers(numbers)})`}</Box>
    </>;
}

function NoteNumber({ number }: { number: number }) {
    return <Box aria-hidden="true" sx={{ flexShrink: 0, width: 22, height: 22, mt: '1px', borderRadius: '50%', bgcolor: STUDENT_BLUE, color: '#fff',
        display: 'grid', placeItems: 'center', fontFamily: STUDENT_INTER, fontWeight: 700, fontSize: 12 }}>{number}</Box>;
}

function NoteExplanation({ item, number, change }: { item: LanguageItem; number: number; change?: React.ReactNode }) {
    return <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <NoteNumber number={number} />
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: STUDENT_INTER, fontWeight: 700, fontSize: 12, color: STUDENT_NAVY }}>
                <Box component="span" sx={visuallyHidden}>Note {number}: </Box>{item.heading}</Typography>
            {change}
            <Typography sx={{ ...bodySx, mt: 0.5 }}>{item.explanation}</Typography>
            {item.new_example_zh && <Typography sx={{ ...noteSx, mt: 0.5 }}>
                Example: <span lang="zh">{item.new_example_zh}</span>{item.new_example_en ? ` — ${item.new_example_en}` : ''}</Typography>}
        </Box>
    </Stack>;
}

const joinNumbers = (numbers: number[]) => numbers.length > 1
    ? `${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}` : String(numbers[0]);

// One sentence's notes: a single note shows its own change; several share one combined sentence.
function NoteGroupView({ group }: { group: NoteGroup }) {
    const combined = group.notes.filter(note => !note.separate);
    if (group.notes.length === 1 || !group.sentence || combined.length < 2) {
        return <>{group.notes.map(({ item, number, heard }) => <Box component="li" key={number}>
            <NoteExplanation item={item} number={number} change={group.sentence
                ? <SentenceChange before={group.sentence} after={placedRevision(item)} />
                : <Typography lang="zh" sx={{ ...bodySx, mt: 0.25 }}>{heard ?? item.original_span} → {item.revised}</Typography>} />
        </Box>)}</>;
    }
    return <Box component="li" sx={{ p: 2, borderRadius: STUDENT_TILE_RADIUS, bgcolor: STUDENT_SURFACE }}>
        <Typography sx={{ fontFamily: STUDENT_INTER, fontWeight: 700, fontSize: 12, color: STUDENT_SLATE }}>
            Notes {joinNumbers(combined.map(note => note.number))} together</Typography>
        <Box sx={{ mt: 0.5 }}><CombinedChange sentence={group.sentence} edits={group.edits} /></Box>
        <Stack component="ol" spacing={1.5} sx={{ m: 0, mt: 1.5, p: 0, listStyle: 'none' }}>
            {group.notes.map(({ item, number, separate }) => <Box component="li" key={number}>
                <NoteExplanation item={item} number={number} change={separate && group.sentence ? <>
                    <Typography sx={{ ...noteSx, mt: 0.25 }}>This note rewrites the same words another way:</Typography>
                    <SentenceChange before={group.sentence} after={placedRevision(item)} />
                </> : undefined} />
            </Box>)}
        </Stack>
    </Box>;
}

// The answer with each language note's span underlined and numbered. Overlapping spans share
// one underline that carries all of their numbers.
function AnnotatedAnswer({ text, items }: { text: string; items: LanguageItem[] }) {
    const groups: { start: number; end: number; numbers: number[] }[] = [];
    items.map((item, index) => ({ start: item.span_start, end: item.span_end, number: index + 1 }))
        .filter((mark): mark is { start: number; end: number; number: number } =>
            typeof mark.start === 'number' && typeof mark.end === 'number' && mark.end <= text.length && mark.start < mark.end)
        .sort((a, b) => a.start - b.start || a.number - b.number)
        .forEach(mark => {
            const last = groups[groups.length - 1];
            if (last && mark.start < last.end) {
                last.end = Math.max(last.end, mark.end);
                last.numbers.push(mark.number);
            } else groups.push({ start: mark.start, end: mark.end, numbers: [mark.number] });
        });
    const parts: React.ReactNode[] = [];
    let cursor = 0;
    for (const group of groups) {
        if (group.start > cursor) parts.push(text.slice(cursor, group.start));
        const chars = Array.from(text.slice(group.start, group.end));
        // The last character stays on the same line as its note numbers.
        parts.push(<Box component="span" key={group.start} sx={{ textDecoration: 'underline', textDecorationColor: STUDENT_BLUE, textDecorationThickness: '2px', textUnderlineOffset: '4px' }}>
            {chars.slice(0, -1).join('')}<Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                {chars[chars.length - 1]}<NoteMark numbers={group.numbers} label="language note" />
            </Box>
        </Box>);
        cursor = group.end;
    }
    parts.push(text.slice(cursor));
    return <Typography lang="zh" sx={{ fontFamily: STUDENT_POPPINS, fontSize: 18, lineHeight: 1.75, color: STUDENT_NAVY, whiteSpace: 'pre-wrap' }}>{parts}</Typography>;
}

// Segments use the sound-tile grade colors (Good, Close, Needs practice), so a color means the same grade
// everywhere; the counts are also written out, so no grade depends on color alone.
function SoundMeter({ label, counts }: { label: string; counts: { good: number; ok: number; bad: number } }) {
    const total = counts.good + counts.ok + counts.bad;
    const percent = total ? Math.round(100 * counts.good / total) : null;
    const share = (count: number) => total ? `${100 * count / total}%` : '0%';
    const tally = `${counts.good} Good, ${counts.ok} Close, ${counts.bad} Needs practice`;
    return <Box sx={{ flex: 1, minWidth: 120 }}>
        <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontFamily: STUDENT_INTER, fontWeight: 700, fontSize: 12, color: STUDENT_NAVY }}>{label}</Typography>
            <Typography sx={{ fontFamily: STUDENT_INTER, fontWeight: 700, fontSize: 12, color: percent == null ? STUDENT_SLATE : STUDENT_NAVY }}>
                {percent == null ? 'Not graded' : `${percent}% Good`}</Typography>
        </Stack>
        <Box role="img" aria-label={percent == null ? `${label}: not graded` : `${label}: ${percent}% Good (${tally})`}
            sx={{ mt: 0.5, height: 8, borderRadius: 99, bgcolor: STUDENT_RING, overflow: 'hidden', display: 'flex' }}>
            <Box sx={{ width: share(counts.good), bgcolor: STUDENT_GREEN }} />
            <Box sx={{ width: share(counts.ok), bgcolor: STUDENT_YELLOW }} />
            <Box sx={{ width: share(counts.bad), bgcolor: STUDENT_RED }} />
        </Box>
        {total > 0 && <Typography aria-hidden sx={{ ...noteSx, fontSize: 11, mt: 0.5 }}>{tally.replace(/, /g, ' · ')}</Typography>}
    </Box>;
}

function clipLabel(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

// Plays one sentence of the saved recording, fetched on first use. The take is decoded with the
// Web Audio API and the clip played from the decoded samples: a MediaRecorder WebM file has no
// seek index, so seeking an <audio> element to a sentence is unreliable. If the browser cannot
// decode the file, the element is used instead.
function useClipPlayer(stepId: number, attemptId: number) {
    const contextRef = React.useRef<AudioContext | null>(null);
    const bufferRef = React.useRef<AudioBuffer | null>(null);
    const sourceRef = React.useRef<AudioBufferSourceNode | null>(null);
    const elementRef = React.useRef<HTMLAudioElement | null>(null);
    const urlRef = React.useRef<string | null>(null);
    const endRef = React.useRef<number | null>(null);
    // The recording is fetched and decoded once, however many taps arrive while it loads; a tap
    // (or Stop) invalidates earlier ones, so only the latest request ever starts playing.
    const loadRef = React.useRef<Promise<void> | null>(null);
    const tokenRef = React.useRef(0);
    const [playing, setPlaying] = React.useState<number | null>(null);
    const [failed, setFailed] = React.useState(false);

    const halt = React.useCallback(() => {
        const source = sourceRef.current;
        sourceRef.current = null;
        if (source) {
            source.onended = null;
            try { source.stop(); } catch { /* already stopped */ }
        }
        elementRef.current?.pause();
    }, []);
    React.useEffect(() => () => {
        halt();
        void contextRef.current?.close().catch(() => undefined);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    }, [halt]);

    const stop = React.useCallback(() => { tokenRef.current += 1; halt(); setPlaying(null); }, [halt]);
    const play = React.useCallback(async (index: number, startMs: number, endMs: number) => {
        const token = ++tokenRef.current;
        setFailed(false);
        halt();
        // Create and resume the context inside the click, before any await, so browsers that
        // require a user gesture for audio (Safari) allow it.
        const Context = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!contextRef.current && Context) contextRef.current = new Context();
        const resumed = contextRef.current?.resume().catch(() => undefined);
        try {
            if (!bufferRef.current && !elementRef.current) {
                loadRef.current ??= (async () => {
                    const recording = (await VerbaScholaApi.getStudentOpenResponseAudio(stepId, attemptId)).data;
                    try {
                        if (!contextRef.current) throw new Error('Web Audio is unavailable');
                        bufferRef.current = await contextRef.current.decodeAudioData(await recording.arrayBuffer());
                    } catch {
                        urlRef.current = URL.createObjectURL(recording);
                        const element = new Audio(urlRef.current);
                        element.addEventListener('timeupdate', () => {
                            if (endRef.current != null && element.currentTime * 1000 >= endRef.current) { element.pause(); setPlaying(null); }
                        });
                        element.addEventListener('ended', () => setPlaying(null));
                        elementRef.current = element;
                    }
                })().catch(error => { loadRef.current = null; throw error; }); // a failed download can be retried
                await loadRef.current;
            }
            if (token !== tokenRef.current) return; // a later tap or Stop took over while loading
            const context = contextRef.current;
            const buffer = bufferRef.current;
            if (context && buffer) {
                await resumed;
                const source = context.createBufferSource();
                source.buffer = buffer;
                source.connect(context.destination);
                source.onended = () => { if (sourceRef.current === source) { sourceRef.current = null; setPlaying(null); } };
                const offset = Math.min(startMs / 1000, buffer.duration);
                source.start(0, offset, Math.max(0, Math.min(endMs / 1000, buffer.duration) - offset));
                sourceRef.current = source;
                setPlaying(index);
                return;
            }
            const element = elementRef.current;
            if (!element) throw new Error('No playable recording');
            endRef.current = endMs;
            element.currentTime = startMs / 1000;
            await element.play();
            if (token === tokenRef.current) setPlaying(index);
        } catch {
            if (token !== tokenRef.current) return;
            setPlaying(null);
            setFailed(true);
        }
    }, [stepId, attemptId, halt]);
    return { playing, failed, play, stop };
}

const MAX_PRACTICE_CHARACTERS = 70; // the Pronunciation page's sentence limit
const MAX_PRACTICE_OFFERED = 5;
const PRACTICE_CHECKED = 3;

interface PracticeCandidate { text: string; notes: number[]; alternative: boolean; tooLong: boolean; issues: number }

type PlacedGroup = NoteGroup & { sentence: string; start: number; end: number };

/** Recorded sentences with sounds marked Close or Needs practice, worst first. A recorded sentence
 *  is matched to the language notes by its place in the answer (answer_start/answer_end), never by
 *  its text, so repeated wording is not confused. Notes placed in its written sentence (those that
 *  change only their quoted words) have their combined changes applied; a note that rewrites the
 *  same words another way, and a note that quotes this sentence but could not be placed, are
 *  offered as their own sentences. Otherwise the sentence is offered as written or recorded. */
export function practiceCandidates(pronunciation: IOpenResponsePronunciation, groups: NoteGroup[], answerText: string): PracticeCandidate[] {
    const placed = groups.filter((group): group is PlacedGroup => group.sentence != null && group.start != null && group.end != null)
        .sort((a, b) => a.start - b.start);
    const unplaced = groups.filter(group => group.sentence == null).flatMap(group => group.notes);
    const candidates = new Map<string, PracticeCandidate>();
    const offer = (text: string, notes: number[], alternative: boolean, issues: number) => {
        if (!text) return;
        const existing = candidates.get(text);
        if (existing) existing.issues += issues;
        else candidates.set(text, { text, notes, alternative, tooLong: Array.from(text).length > MAX_PRACTICE_CHARACTERS, issues });
    };
    for (const sentence of pronunciation.sentences) {
        if (sentence.status !== 'scored') continue;
        const issues = sentence.characters.reduce((total, row) => total + (['initial_grade', 'final_grade', 'tone_grade'] as const)
            .reduce((sum, part) => sum + (row[part] === 'bad' ? 2 : row[part] === 'ok' ? 1 : 0), 0), 0);
        if (!issues) continue;
        const { answer_start: from, answer_end: to } = sentence;
        if (from == null || to == null) {
            offer(sentence.text.trim(), [], false, issues);
            continue;
        }
        const covering = placed.filter(group => group.start < to && from < group.end);
        let text = '';
        let cursor = Math.min(from, ...covering.map(group => group.start));
        const used: PlacedGroup[] = [];
        for (const group of covering) {
            if (group.start < cursor) continue; // overlaps a group already applied
            text += answerText.slice(cursor, group.start) + applyEdits(group.sentence, group.edits);
            cursor = group.end;
            used.push(group);
        }
        text += answerText.slice(cursor, Math.max(to, ...covering.map(group => group.end)));
        offer(text.trim(), used.flatMap(group => group.notes.filter(note => !note.separate).map(note => note.number)), false, issues);
        used.forEach(group => group.notes.filter(note => note.separate).forEach(note => offer(placedRevision(note.item).trim(), [note.number], true, issues)));
        // A note whose change could not be placed is offered as its own suggested sentence, never spliced into this one.
        unplaced.filter(note => {
            const at = note.item.span_start ?? note.item.recording_start;
            return at != null && from <= at && at < to;
        }).forEach(note => offer(note.item.revised.trim(), [note.number], true, issues));
    }
    return [...candidates.values()].sort((a, b) => b.issues - a.issues).slice(0, MAX_PRACTICE_OFFERED);
}

function practiceError(error: unknown): string {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 491) return 'You can add up to 10 practice sentences every 30 minutes. Try the rest later.';
    if (status === 490) return 'Verify your email address to add practice sentences.';
    return 'Some sentences could not be added. Try again later.';
}

// Adds the chosen sentences to the student's Pronunciation practice list after confirmation.
function PracticeSentencesDialog({ open, candidates, speakOnly, onClose }: { open: boolean; candidates: PracticeCandidate[]; speakOnly: boolean; onClose: () => void }) {
    const [existing, setExisting] = React.useState<Set<string>>(new Set());
    const [checked, setChecked] = React.useState<Set<string>>(new Set());
    const [adding, setAdding] = React.useState(false);
    const [result, setResult] = React.useState<{ added: number; error: string } | null>(null);
    const candidatesRef = React.useRef(candidates);
    candidatesRef.current = candidates;

    React.useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setResult(null);
        const addable = candidatesRef.current.filter(candidate => !candidate.tooLong);
        setChecked(new Set(addable.slice(0, PRACTICE_CHECKED).map(candidate => candidate.text)));
        void CustomSentenceApi.getAll().then(response => {
            if (cancelled) return;
            const saved = new Set(response.data.map(sentence => sentence.text.trim()));
            setExisting(saved);
            setChecked(new Set(addable.filter(candidate => !saved.has(candidate.text)).slice(0, PRACTICE_CHECKED).map(candidate => candidate.text)));
        }).catch(() => undefined); // without the list, no sentence is marked as already saved
        return () => { cancelled = true; };
    }, [open]);

    const toggle = (text: string) => setChecked(current => {
        const next = new Set(current);
        if (next.has(text)) next.delete(text); else next.add(text);
        return next;
    });
    const add = async () => {
        setAdding(true);
        let added = 0;
        let error = '';
        for (const text of candidates.map(candidate => candidate.text).filter(text => checked.has(text))) {
            try {
                // The same route as adding a sentence on the Pronunciation page: server pinyin rules, then save.
                const pinyin = (await SentenceApi.getTextPinyin(text)).data.flat();
                await CustomSentenceApi.create({ id: 0, text, pinyin });
                added += 1;
            } catch (failure) {
                error = practiceError(failure);
                break;
            }
        }
        setAdding(false);
        setResult({ added, error });
    };

    return <Dialog open={open} onClose={adding ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: STUDENT_CARD_RADIUS } }}>
        <DialogTitle sx={{ fontFamily: STUDENT_POPPINS, fontWeight: 600, color: STUDENT_NAVY }}>Practice these sentences?</DialogTitle>
        <DialogContent>
            {result ? <Stack spacing={1.5}>
                {result.added > 0 && <Alert severity="success">Added {result.added} {result.added === 1 ? 'sentence' : 'sentences'} to Pronunciation practice.</Alert>}
                {result.error && <Alert severity="warning">{result.error}</Alert>}
                {result.added > 0 && <Typography sx={bodySx}>Record them on the Pronunciation practice page, now or later. It opens in a new tab, so this answer stays as it is.</Typography>}
            </Stack> : <>
                <Typography sx={bodySx}>
                    These sentences will be added to your Pronunciation practice page, where you can record each one and see how every sound was heard.
                    Where a correction changes only the words it quotes, the sentence is added with that change. Other suggested wordings are offered as their own sentences, and the rest as you {speakOnly ? 'recorded' : 'wrote'} them.
                </Typography>
                <Stack sx={{ mt: 1.5 }}>
                    {candidates.map(candidate => {
                        const saved = existing.has(candidate.text);
                        const disabled = candidate.tooLong || saved;
                        const note = candidate.tooLong ? `Too long to practice as one sentence (over ${MAX_PRACTICE_CHARACTERS} characters)`
                            : saved ? 'Already in your practice list'
                            : candidate.alternative ? `Note ${candidate.notes[0]}'s suggested wording`
                            : candidate.notes.length > 1 ? `Corrected wording, with notes ${joinNumbers(candidate.notes)}`
                            : candidate.notes.length === 1 ? `Corrected wording, with note ${candidate.notes[0]}`
                            : speakOnly ? 'As we heard it' : 'Your wording';
                        return <FormControlLabel key={candidate.text} disabled={disabled}
                            control={<Checkbox checked={!disabled && checked.has(candidate.text)} onChange={() => toggle(candidate.text)} />}
                            label={<Box sx={{ py: 0.5 }}>
                                <Typography lang="zh" sx={{ fontFamily: STUDENT_POPPINS, fontSize: 16, color: STUDENT_NAVY }}>{candidate.text}</Typography>
                                <Typography sx={noteSx}>{note}</Typography>
                            </Box>} sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 1 } }} />;
                    })}
                </Stack>
            </>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
            {result ? <>
                <Button onClick={onClose} sx={{ minHeight: 44 }}>Close</Button>
                {result.added > 0 && <Button component="a" href="/student/pronunciation" target="_blank" rel="noopener" variant="contained" sx={studentBlueButtonSx}>
                    Open Pronunciation practice<Box component="span" sx={visuallyHidden}> (opens in a new tab)</Box></Button>}
            </> : <>
                <Button onClick={onClose} disabled={adding} sx={{ minHeight: 44 }}>Cancel</Button>
                <Button onClick={() => void add()} disabled={adding || checked.size === 0} variant="contained" sx={studentBlueButtonSx}>
                    {adding ? 'Adding…' : `Add ${checked.size} ${checked.size === 1 ? 'sentence' : 'sentences'}`}
                </Button>
            </>}
        </DialogActions>
    </Dialog>;
}

function PronunciationSentences({ stepId, attempt, pronunciation }: { stepId: number; attempt: IStudentOpenResponseAttempt; pronunciation: IOpenResponsePronunciation }) {
    const player = useClipPlayer(stepId, attempt.id);
    return <Stack spacing={1.5} sx={{ mt: 1.25 }}>
        {player.failed && <Typography role="alert" sx={{ ...noteSx, color: STUDENT_ERROR_TEXT }}>This part of your recording could not be played.</Typography>}
        {pronunciation.sentences.map(sentence => {
            const canPlay = attempt.has_audio && sentence.start_ms != null && sentence.end_ms != null;
            const isPlaying = player.playing === sentence.index;
            return <Box key={sentence.index} sx={{ p: 1.5, borderRadius: STUDENT_TILE_RADIUS, border: `1px solid ${STUDENT_RING}`, bgcolor: '#fff' }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {canPlay && <Button onClick={() => isPlaying ? player.stop() : void player.play(sentence.index, sentence.start_ms ?? 0, sentence.end_ms ?? 0)}
                        startIcon={isPlaying ? <StopRoundedIcon /> : <PlayArrowRoundedIcon />}
                        aria-label={`${isPlaying ? 'Stop' : 'Play'} this sentence of your recording, from ${clipLabel(sentence.start_ms ?? 0)}`}
                        sx={{ ...studentSecondaryButtonSx, px: 1.5 }}>
                        {clipLabel(sentence.start_ms ?? 0)}
                    </Button>}
                    <Typography lang="zh" sx={{ fontFamily: STUDENT_POPPINS, fontSize: 16, color: STUDENT_NAVY }}>{sentence.text}</Typography>
                </Stack>
                {sentence.heard_text && <Box sx={{ mt: 1 }}><StudentCautionNote icon={HearingRoundedIcon} compact>
                    We heard <span lang="zh">{sentence.heard_text}</span>. Where your words differ from your writing, these marks may not fit what you said.
                </StudentCautionNote></Box>}
                {sentence.status === 'scored' && sentence.characters.length > 0
                    ? <Box sx={{ mt: 1 }}><StudentPronunciationDetail score={{ character_scores: sentence.characters }} showPinyin showLegend={false} showGuidance={false} compact /></Box>
                    : <Typography sx={{ ...noteSx, mt: 0.75 }}>{unscoredMessages[sentence.status] ?? 'The listening check was unavailable for this sentence, so it was not checked.'}</Typography>}
            </Box>;
        })}
        <Box><StudentPronunciationLegend showPinyin /><Typography sx={{ ...noteSx, mt: 0.75, textAlign: 'center' }}>Needs practice is shown only when the check is confident.</Typography></Box>
    </Stack>;
}

export default function VerbaScholaStudentOpenResponseFeedback({ stepId, attempt, speakOnly, onRevise }: Props) {
    const [showSounds, setShowSounds] = React.useState(false);
    const [practiceOpen, setPracticeOpen] = React.useState(false);
    const soundsId = React.useId();
    const items = languageItems(attempt);
    const tips = attempt.feedback?.pronunciation_items ?? [];
    const pronunciation = attempt.pronunciation ?? null;
    const answerText = (speakOnly ? attempt.transcript_text : attempt.written_text) ?? '';
    const groups = React.useMemo(() => noteGroups(languageItems(attempt), answerText), [attempt, answerText]);
    const candidates = React.useMemo(() => pronunciation ? practiceCandidates(pronunciation, groups, answerText) : [],
        [pronunciation, groups, answerText]);
    const graded = pronunciation ? Object.values(pronunciation.summary).some(counts => counts.good + counts.ok + counts.bad > 0) : false;
    const topPattern = pronunciation?.patterns[0];
    const focus: React.ReactNode[] = [];
    if (items[0]) focus.push(<>Language note 1: <span lang="zh">{items[0].original_span}</span> ({items[0].heading.toLowerCase()})</>);
    if (topPattern) focus.push(<>{topPattern.label} ({topPattern.count} times)</>);
    else if (tips[0]) focus.push(<>Pronunciation: <span lang="zh">{tips[0].intended ?? tips[0].text}</span>{tips[0].expected_pinyin ? ` ${instructionalPinyin(tips[0].expected_pinyin.split(' '))}` : ''}</>);

    return <Stack spacing={2.5} sx={{ mt: 1.5 }}>
        {(focus.length > 0 || onRevise) && <Box sx={{ p: 1.75, borderRadius: STUDENT_TILE_RADIUS, bgcolor: STUDENT_SURFACE }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <TrackChangesRoundedIcon aria-hidden="true" sx={{ fontSize: 19, color: STUDENT_BLUE }} />
                <Typography component="h4" sx={sectionTitleSx}>Focus next time</Typography>
            </Stack>
            {focus.length > 0
                ? <Box component="ol" sx={{ m: 0, mt: 1, pl: 2.5 }}>{focus.map((item, index) => <Typography component="li" key={index} sx={bodySx}>{item}</Typography>)}</Box>
                : <Typography sx={{ ...bodySx, mt: 1 }}>No correction was suggested for this answer.</Typography>}
            {onRevise && <Button onClick={onRevise} startIcon={<EditRoundedIcon />} sx={{ ...studentSecondaryButtonSx, mt: 1.25, bgcolor: '#fff' }}>
                Revise your answer
            </Button>}
        </Box>}

        {pronunciation && pronunciation.status !== 'checked' && <Alert severity="warning" icon={<HearingRoundedIcon />}>
            {pronunciation.status === 'no_speech'
                ? 'We could not hear speech in your recording, so your pronunciation was not checked. Play it back to check your microphone, then record again.'
                : 'The listening check was unavailable for this recording, so this feedback covers your writing only. To have your pronunciation checked, record again and ask for feedback.'}
        </Alert>}

        {pronunciation && pronunciation.status === 'checked' && !graded && pronunciation.sentences.length > 0
            && <Alert severity="info" icon={<HearingRoundedIcon />}>
                The sounds in this recording could not be checked this time. Open Sentence by sentence below to see why for each part and to play it.
            </Alert>}

        {graded && pronunciation && <Box>
            <Typography component="h4" sx={sectionTitleSx}>Sounds in this recording</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.25, sm: 2 }} sx={{ mt: 1.25 }}>
                <SoundMeter label="Initials" counts={pronunciation.summary.initial} />
                <SoundMeter label="Finals" counts={pronunciation.summary.final} />
                <SoundMeter label="Tones" counts={pronunciation.summary.tone} />
            </Stack>
            <StudentPronunciationLegend showPinyin={false} showBars={false} />
            <Typography sx={{ ...noteSx, mt: 0.75 }}>Share of checked sounds marked Good by an automatic listening check, which can mishear. Parts it could not check are not counted.</Typography>
        </Box>}

        {answerText && <Box>
            <Typography component="h4" sx={sectionTitleSx}>{speakOnly ? 'What we heard' : 'Your answer'}</Typography>
            {speakOnly && <Typography sx={{ ...noteSx, mt: 0.5 }}>An automatic transcript of your recording. It may have missed words, so listen to your recording to compare.</Typography>}
            <Box sx={{ mt: 1 }}><AnnotatedAnswer text={answerText} items={items} /></Box>
        </Box>}

        {pronunciation && pronunciation.sentences.length > 0 && <Box>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                <Typography component="h4" sx={sectionTitleSx}>Sentence by sentence</Typography>
                <StudentDisclosureButton label="See sound details" open={showSounds} onToggle={() => setShowSounds(open => !open)} controls={soundsId} />
            </Stack>
            {!showSounds && <Typography sx={noteSx}>Play each recorded sentence and see how each sound was heard.</Typography>}
            <Box id={soundsId}>{showSounds && <PronunciationSentences stepId={stepId} attempt={attempt} pronunciation={pronunciation} />}</Box>
        </Box>}

        <Box>
            <Typography component="h4" sx={sectionTitleSx}>Language{items.length ? ` (${items.length})` : ''}</Typography>
            {speakOnly && items.length > 0 && <Typography sx={{ ...noteSx, mt: 0.5 }}>Based on the automatic transcript, so check that it matches what you meant.</Typography>}
            {items.length ? <Stack component="ul" spacing={1.75} sx={{ m: 0, mt: 1.25, p: 0, listStyle: 'none' }}>
                {groups.map((group, index) => <NoteGroupView key={index} group={group} />)}
            </Stack> : <Typography sx={{ ...bodySx, mt: 1 }}>No correction was suggested for this answer.</Typography>}
        </Box>

        {(Boolean(pronunciation?.patterns.length) || tips.length > 0 || candidates.length > 0) && <Box>
            <Typography component="h4" sx={sectionTitleSx}>Pronunciation to practice</Typography>
            <Typography sx={{ ...noteSx, mt: 0.5 }}>From an automatic listening check, which can mishear. Listen to your recording before you change anything.</Typography>
            {candidates.length > 0 && <>
                <Button onClick={() => setPracticeOpen(true)} startIcon={<RecordVoiceOverRoundedIcon />} sx={{ ...studentSecondaryButtonSx, mt: 1.25 }}>
                    Practice these sounds
                </Button>
                <Typography sx={{ ...noteSx, mt: 0.5 }}>Adds the sentences with sounds to work on, in their corrected wording, to your Pronunciation practice. You can choose which ones first.</Typography>
                <PracticeSentencesDialog open={practiceOpen} candidates={candidates} speakOnly={speakOnly} onClose={() => setPracticeOpen(false)} />
            </>}
            {pronunciation && pronunciation.patterns.length > 0 && <Box sx={{ mt: 1.25, p: 1.5, borderRadius: STUDENT_TILE_RADIUS, bgcolor: STUDENT_SURFACE }}>
                <Typography sx={studentEyebrowSx}>PATTERNS IN THIS ANSWER</Typography>
                <Stack component="ul" spacing={0.75} sx={{ m: 0, mt: 1, p: 0, listStyle: 'none' }}>
                    {pronunciation.patterns.map(pattern => <Typography component="li" key={pattern.key} sx={bodySx}>
                        <Box component="span" sx={{ fontWeight: 700 }}>{pattern.label}</Box>
                        <Box component="span" sx={{ color: STUDENT_SLATE }}> · {pattern.count} times · e.g. </Box>
                        {pattern.examples.map((example, index) => <React.Fragment key={index}>
                            {index > 0 && ', '}<span lang="zh">{example.character}</span> {instructionalPinyin([example.expected_pinyin])}
                            <Box component="span" sx={{ color: STUDENT_ERROR_TEXT }}> (heard {instructionalPinyin([example.spoken_pinyin])})</Box>
                        </React.Fragment>)}
                    </Typography>)}
                </Stack>
            </Box>}
            {tips.map((item, index) => <Box key={index} sx={{ mt: 1.25, p: 1.5, borderRadius: STUDENT_TILE_RADIUS, border: `1px solid ${STUDENT_RING}`,
                borderLeft: `4px solid ${item.strength === 'likely' ? STUDENT_RED : STUDENT_YELLOW}` }}>
                <Typography sx={{ fontFamily: STUDENT_INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.06em', color: STUDENT_NAVY }}>
                    {item.strength === 'likely' ? 'WORK ON THIS' : 'TRY THIS'}</Typography>
                <Typography sx={{ ...bodySx, fontWeight: 700, mt: 0.25 }}>
                    <span lang="zh">{item.intended ?? item.text}</span>
                    {item.expected_pinyin ? ` · ${instructionalPinyin(item.expected_pinyin.split(' '))}` : ''}</Typography>
                {item.intended && item.intended !== item.text && <Typography sx={noteSx}>
                    The automatic transcript wrote <span lang="zh">{item.text}</span>.</Typography>}
                <Typography sx={{ ...bodySx, mt: 0.5 }}>{item.tip}</Typography>
            </Box>)}
        </Box>}
    </Stack>;
}
